import { Add } from "@mui/icons-material";
import { Chip, ChipProps, Fab, Stack, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsArrayOf, parseAsString, parseAsStringEnum } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlPermissions, GqlProjectStatus } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";
import ProjectStatusSelect from "~/modules/project/components/ProjectStatusSelect";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import ErrorScreen from "../../../app/screens/ErrorScreen";
import useCreateProjectUrl from "../../hooks/useCreateProjectUrl";
import useProjectListScreen_GetAll from "./api/useProjectListScreen_GetAll";

export default function ProjectListScreen() {
    const { t } = useTranslation("project");

    const createProjectUrl = useCreateProjectUrl();
    const { client } = useRequireCurrentClient();
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
        statuses: parseAsArrayOf(parseAsStringEnum<GqlProjectStatus>(Object.values(GqlProjectStatus))).withDefault(
            isAdmin ? DEFAULT_STATUSES_ADMIN : DEFAULT_STATUSES_CLIENT,
        ),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const { data, loading, error, totalRows } = useProjectListScreen_GetAll({
        ...queryState,
        clientId: client.id,
        q: debouncedQ || undefined,
    });

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("projectListScreen.colName"),
                flex: 1,
            },
            {
                field: "status",
                headerName: t("projectListScreen.colStatus"),
                flex: 1,
                renderCell: row => {
                    const color = STATUS_COLOR[row.value as GqlProjectStatus];
                    return <Chip label={t(`projectListScreen.projectStatusValue.${row.value}`)} size="small" color={color} />;
                },
            },
            {
                type: "actions",
                field: "actions",
                valueGetter: (_, row) => createProjectUrl("", { project: row }),
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t, createProjectUrl],
    );

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 });
    };

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("projectListScreen.title")}
                actions={
                    isAdmin && (
                        <Fab component={Link} to="./new-project" color="primary">
                            <Add />
                        </Fab>
                    )
                }
            >
                <Stack direction="row" gap={2}>
                    <TextField
                        label={t("projectListScreen.filterName")}
                        variant="outlined"
                        value={queryState.q}
                        onChange={e => handleParameterChange("q", e.target.value)}
                        sx={{ width: "25%" }}
                    />
                    <ProjectStatusSelect
                        value={queryState.statuses}
                        onChange={nextValue => handleParameterChange("statuses", nextValue)}
                        sx={{ width: "fit-content", minWidth: "25%" }}
                    />
                </Stack>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}

const STATUS_COLOR: Record<GqlProjectStatus, ChipProps["color"]> = {
    [GqlProjectStatus.Cancelled]: "error",
    [GqlProjectStatus.Open]: "secondary",
    [GqlProjectStatus.Confirmed]: "primary",
    [GqlProjectStatus.Completed]: "success",
};

const DEFAULT_STATUSES_ADMIN = [GqlProjectStatus.Open, GqlProjectStatus.Confirmed];
const DEFAULT_STATUSES_CLIENT = [GqlProjectStatus.Open, GqlProjectStatus.Completed, GqlProjectStatus.Confirmed];
